import dayjs from "dayjs";

import React, { PureComponent } from "react";

import { Box, CircularProgress, useTheme, useMediaQuery } from "@mui/material";

import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import {
  useGetTotalTalentByDateQuery,
  useGetTotalCompanyByDateQuery,
} from "../../services/report.service";

function formatXAxis(tickItem: string) {
  return dayjs(tickItem).format("DD MMM YYYY");
}

interface ParamsProps {
  startDate: string;
  endDate: string;
}

interface BarChartNewTalentCompanyProps {
  id?: string;
  params: ParamsProps;
  useDummyData?: boolean;
}

// Generate dummy data for testing with many data points
// Maksimal 5000 data points untuk performa
const generateDummyData = (startDate: string, endDate: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const days = end.diff(start, "day") + 1;
  const maxDataPoints = 5000; // Batasi maksimal 5000 data points

  const dummyData: any[] = [];

  // Jika lebih dari 5000 hari, ambil sample dengan step yang lebih kecil
  // Maksimal step 7 hari (seminggu) agar tidak terlalu jauh
  if (days > maxDataPoints) {
    const step = Math.ceil(days / maxDataPoints);
    // Batasi step maksimal 7 hari agar tidak terlalu jauh
    const maxStep = 7;
    const actualStep = Math.min(step, maxStep);

    // Hitung berapa banyak data points yang akan dihasilkan dengan step terbatas
    const estimatedPoints = Math.ceil(days / actualStep);
    const targetPoints = Math.min(estimatedPoints, maxDataPoints);

    for (let i = 0; i < targetPoints && dummyData.length < maxDataPoints; i++) {
      const dayIndex = i * actualStep;
      const date = start.add(dayIndex, "day");

      // Pastikan tidak melebihi end date
      if (date.isAfter(end)) break;

      dummyData.push({
        date: date.format("YYYY-MM-DD"),
        total: Math.floor(Math.random() * 50) + 10, // Random between 10-60
      });

      // Safety check: pastikan tidak lebih dari maxDataPoints
      if (dummyData.length >= maxDataPoints) break;
    }
  } else {
    // Jika kurang dari 5000 hari, generate semua
    for (let i = 0; i < days && dummyData.length < maxDataPoints; i++) {
      const date = start.add(i, "day");
      dummyData.push({
        date: date.format("YYYY-MM-DD"),
        total: Math.floor(Math.random() * 50) + 10, // Random between 10-60
      });
    }
  }

  // Final safeguard: pastikan tidak lebih dari 5000 data points
  return dummyData.slice(0, maxDataPoints);
};

const BarChartNewTalentCompany = (props: BarChartNewTalentCompanyProps) => {
  const { id, params, useDummyData = false } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: dataTotalTalentByDate,
    isFetching: isFetchingTotalTalentByDate,
    isLoading: isLoadingTotalTalentByDate,
  } = useGetTotalTalentByDateQuery(params, { skip: useDummyData });

  const {
    data: dataTotalCompanyByDate,
    isFetching: isFetchingTotalCompanyByDate,
    isLoading: isLoadingTotalCompanyByDate,
  } = useGetTotalCompanyByDateQuery(params, { skip: useDummyData });

  // Generate dummy data if enabled - generate sekali untuk memastikan tanggal sama
  const dummyDataBase = React.useMemo(
    () => (useDummyData ? generateDummyData(params.startDate, params.endDate) : null),
    [useDummyData, params.startDate, params.endDate]
  );

  // Gunakan data base yang sama untuk talent dan company agar tanggal sama
  const dummyTalentData = React.useMemo(
    () => dummyDataBase?.map(item => ({ ...item })),
    [dummyDataBase]
  );
  const dummyCompanyData = React.useMemo(
    () => dummyDataBase?.map(item => ({ ...item })),
    [dummyDataBase]
  );

  const talentData = useDummyData ? dummyTalentData : dataTotalTalentByDate;
  const companyData = useDummyData ? dummyCompanyData : dataTotalCompanyByDate;

  const totalTalent = React.useMemo(
    () =>
      talentData
        ?.map((it) => it.total)
        ?.reduce((prev, cur) => prev + cur, 0) || 0,
    [talentData]
  );

  const totalCustomer = React.useMemo(
    () =>
      companyData
        ?.map((it) => it.total)
        ?.reduce((prev, cur) => prev + cur, 0) || 0,
    [companyData]
  );

  const chartData = React.useMemo(() => {
    let newData: any[] = [];
    talentData?.map((item) => {
      newData.push({ date: item.date, talent: item.total, customer: 0 });
    });
    companyData?.map((item) => {
      const curData = newData.find((curItem) => curItem.date === item.date);
      if (curData) {
        newData.find((curItem) => curItem.date === item.date).customer +=
          item.total;
      } else {
        newData.push({ date: item.date, talent: 0, customer: item.total });
      }
    });
    newData.sort((a, b) => {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return dayjs(a.date).diff(b.date);
    });

    // Batasi maksimal 5000 data points untuk dummy data
    if (useDummyData && newData.length > 5000) {
      return newData.slice(0, 5000);
    }

    return newData;
  }, [talentData, companyData, useDummyData]);

  // Calculate interval untuk mencegah overlap saat banyak data points
  // Untuk range tahunan di mobile, tampilkan setiap 10 hari (1, 10, 20, 30, dst)
  // Handling untuk data real dari API juga akan bekerja dengan baik
  const xAxisInterval = React.useMemo(() => {
    if (!chartData || chartData.length === 0) return 0;
    const dataPoints = chartData.length;

    if (isMobile) {
      // Mobile: untuk range tahunan, tampilkan setiap 10 hari
      if (dataPoints <= 15) return 0; // Show all jika sedikit
      if (dataPoints > 1000) return Math.floor(dataPoints / 100); // Untuk data sangat banyak, tampilkan ~100 labels
      if (dataPoints > 365) return 9; // Setiap 10 hari (interval 9 = tampilkan index 0, 10, 20, 30...)
      if (dataPoints > 180) return 9; // Setiap 10 hari
      if (dataPoints > 90) return 4; // Setiap 5 hari
      if (dataPoints > 30) return 2; // Setiap 3 hari
      return 0; // Show all jika sedikit
    } else {
      // Desktop: sama seperti mobile, setiap 10 hari untuk range tahunan
      if (dataPoints <= 15) return 0; // Show all jika sedikit
      if (dataPoints > 1000) return Math.floor(dataPoints / 200); // Untuk data sangat banyak, tampilkan ~200 labels
      if (dataPoints > 365) return 9; // Setiap 10 hari (interval 9 = tampilkan index 0, 10, 20, 30...)
      if (dataPoints > 180) return 9; // Setiap 10 hari
      if (dataPoints > 90) return 4; // Setiap 5 hari
      if (dataPoints > 30) return 2; // Setiap 3 hari
      return 0; // Show all jika sedikit
    }
  }, [chartData, isMobile]);

  // Calculate margin bottom berdasarkan jumlah data points
  // Handling untuk data real dari API juga akan bekerja dengan baik
  const bottomMargin = React.useMemo(() => {
    const dataPoints = chartData?.length || 0;
    if (dataPoints > 1000) return 100; // Sangat besar untuk data sangat banyak
    if (dataPoints > 30) return 80; // Lebih besar untuk banyak data
    if (dataPoints > 15) return 60;
    return 35; // Default
  }, [chartData]);

  // Calculate dynamic height berdasarkan jumlah data points
  // Handling untuk data real dari API juga akan bekerja dengan baik
  const chartHeight = React.useMemo(() => {
    const dataPoints = chartData?.length || 0;
    if (isMobile) {
      if (dataPoints > 1000) return 450; // Untuk data sangat banyak
      if (dataPoints > 365) return 400;
      if (dataPoints > 180) return 380;
      if (dataPoints > 90) return 350;
      if (dataPoints > 30) return 320;
      return 300; // Default
    } else {
      if (dataPoints > 1000) return 420; // Untuk data sangat banyak
      if (dataPoints > 365) return 380;
      if (dataPoints > 180) return 360;
      if (dataPoints > 90) return 340;
      if (dataPoints > 30) return 320;
      return 300; // Default
    }
  }, [chartData, isMobile]);

  // Calculate chart width untuk mobile - lebih lebar agar bisa di-scroll
  const chartWidth = React.useMemo(() => {
    if (!isMobile) return "100%";
    const dataPoints = chartData?.length || 0;
    // Berikan ruang minimal 3px per bar untuk readability
    return Math.max(800, dataPoints * 3);
  }, [chartData, isMobile]);

  if ((isLoadingTotalTalentByDate || isLoadingTotalCompanyByDate) && !useDummyData)
    return (
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box
      sx={{
        width: "100%",
        height: chartHeight,
        overflowX: isMobile ? "auto" : "visible",
        overflowY: "hidden",
        ...(isMobile && {
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.5)",
            },
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.05)",
          },
        }),
      }}
    >
      <ResponsiveContainer
        width={chartWidth}
        height={chartHeight}
      >
        <BarChart
          height={chartHeight}
          data={chartData}
          margin={{
            top: 5,
            right: isMobile ? 10 : 10,
            left: isMobile ? 0 : 10,
            bottom: bottomMargin,
          }}
        >
          {/* <CartesianGrid strokeDasharray="3 3" /> */}
          <XAxis
            angle={-45}
            dataKey="date"
            fontSize="12"
            interval={xAxisInterval}
            textAnchor="end"
            tickFormatter={formatXAxis}
            height={chartData && chartData.length > 30 ? (chartData.length > 1000 ? 100 : 80) : 60}
          />
          <YAxis />
          <Tooltip />
          <Legend
            layout="horizontal"
            verticalAlign="top"
            wrapperStyle={{ paddingBottom: "8px" }}
          />
          <Bar
            name={`Talent: ${totalTalent?.toLocaleString() || 0}`}
            dataKey="talent"
            fill={theme.palette.primary.main}
          />
          <Bar
            name={`Client: ${totalCustomer?.toLocaleString() || 0}`}
            dataKey="customer"
            fill={theme.palette.secondary.main}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default BarChartNewTalentCompany;
